import React from 'react';
import PropTypes from 'prop-types';
import './QrStyleForm.css';
import Select2 from './Select2.jsx';
import QrPreview from './qrStyle/QrPreview.jsx';

// Colors and shapes are not editable on this screen, so QrPreview's default wording
// would send the user looking for controls that are not here.
const PREVIEW_ERROR = 'Preview unavailable — try a different template or size.';

// Stable reference: a new object each render makes Select2 re-init (see QrStyleForm).
// Neither list is long enough to want a search box.
const SELECT2_OPTIONS = { minimumResultsForSearch: Infinity };

class QrDownloadForm extends React.Component {
  constructor(props) {
    super(props);
    // Built once for the same reason: Select2#updSelect2 tears down and re-initialises
    // whenever the `data` prop is a new reference, so mapping inline would rebuild both
    // dropdowns on every keystroke in the caption field.
    this.templateData = props.templates.map((t) => ({ id: t.id, text: t.name }));
    this.sizeData = props.sizes;
    const template = this.templateFor(props.selected_template_id);
    this.state = {
      templateId: template.id,
      size: template.default_download_size,
      transparency: template.transparency,
      subtext: '',
    };
  }

  // Falls back to the first available template rather than crashing: the selected id
  // comes from QrCodeTemplateService, which can return the id-0 Terminus default.
  templateFor(id) {
    const { templates } = this.props;
    return templates.find((t) => t.id === id) || templates[0];
  }

  // Today's page reloads on template change and re-derives size and transparency from
  // the server. Resetting both here preserves that, minus the reload.
  handleTemplateChange(id) {
    const template = this.templateFor(id);
    this.setState({
      templateId: id,
      size: template.default_download_size,
      transparency: template.transparency,
    });
  }

  render() {
    const p = this.props;
    const {
      templateId, size, transparency, subtext,
    } = this.state;

    return (
      <div className="qr-style-form">
        {/* The same centered pair QrStyleForm uses — a fixed-width form column beside an
            auto-width preview, centred as one group. Bootstrap's row/col stretched the
            controls across the whole panel and stranded the preview at the far edge,
            which is exactly what .qr-form-layout exists to prevent. */}
        <div className="qr-form-layout">
          <div className="qr-form-col">
            {/* A real GET form: the two buttons keep pointing at the existing download
                routes via formAction, and the format lives in the path, so the query
                string survives. No JS submission path is needed. */}
            <form method="get" data-testid="download-form">
              <input type="hidden" name="url_type" value={p.url_type || ''} readOnly />

              <div className="qr-field">
                <label className="qr-label" htmlFor="qr-download-template">QR code template</label>
                {/* String(), not the raw number, for both selects. Select2#updateValue
                    compares $el.val() -- always a DOM string -- against this prop with
                    ===, so a number never matches and every componentDidUpdate re-fires
                    `change`, which calls onChange, which setStates, which re-renders:
                    "Maximum update depth exceeded" and React unmounts the whole form.
                    QrStyleForm's size select stringifies for exactly this reason. State
                    stays numeric -- onChange casts back, and templateFor matches on id. */}
                <Select2
                  id="qr-download-template"
                  data-testid="template-select"
                  name="qr_code_template_id"
                  className="form-control"
                  value={String(templateId)}
                  data={this.templateData}
                  options={SELECT2_OPTIONS}
                  onChange={(e) => this.handleTemplateChange(Number(e.target.value))}
                />
              </div>

              <div className="qr-field">
                <label className="qr-label" htmlFor="qr-download-size">Size (px)</label>
                <Select2
                  id="qr-download-size"
                  data-testid="size-select"
                  name="size"
                  className="form-control"
                  value={String(size)}
                  data={this.sizeData}
                  options={SELECT2_OPTIONS}
                  onChange={(e) => this.setState({ size: Number(e.target.value) })}
                />
              </div>

              <div className="qr-field">
                {/* Always submitted, in both directions: an absent transparency param
                    means "use the template's stored styling" on the server, so
                    omitting it when unticked would silently ignore the user. Unnamed
                    checkbox + named hidden input, so exactly one value is submitted. */}
                <input type="hidden" name="transparency" value={transparency ? '1' : '0'} readOnly />
                {/* qr-check-row, the same box-then-label pattern LogoTab uses. NOT
                    qr-label--inline: that one is justify-content: space-between, for a
                    label paired with a value readout ("Logo area … 25%"), and it flung
                    this label to the far edge of the column. */}
                <label className="qr-check-row">
                  <input
                    data-testid="transparency-check"
                    type="checkbox"
                    checked={transparency}
                    onChange={(e) => this.setState({ transparency: e.target.checked })}
                  />
                  <span>Transparent background</span>
                </label>
              </div>

              <div className="qr-field qr-field--last">
                <label className="qr-label" htmlFor="qr-download-subtext">Caption</label>
                <input
                  id="qr-download-subtext"
                  data-testid="subtext-input"
                  type="text"
                  name="subtext"
                  className="form-control"
                  maxLength={p.subtext_limit}
                  value={subtext}
                  onChange={(e) => this.setState({ subtext: e.target.value })}
                />
                <div className="qr-help">Printed under the QR code. Leave blank for none.</div>
              </div>

              <div className="qr-actions">
                <button type="submit" data-testid="download-png" className="btn btn-primary" formAction={p.png_url}>
                  <i className="icon-download position-left" />
                  PNG
                </button>
                {' '}
                <button
                  type="submit"
                  data-testid="download-svg"
                  className="btn btn-primary"
                  formAction={p.svg_url}
                  title="Vector format — scales to any size without quality loss"
                >
                  <i className="icon-download position-left" />
                  SVG
                </button>
              </div>
            </form>
          </div>

          {/* Second in source order, like QrStyleForm, so the mobile rule that pulls
              .qr-preview-col above the form still applies. */}
          <div className="qr-preview-col">
            {/* Fixed size, deliberately NOT the form's `size` state: the selector picks
                the downloaded file's dimensions, and a QR's module grid comes from the
                payload rather than the pixel size, so re-rendering the preview per size
                only made it jump around while showing the same picture. */}
            <QrPreview
              previewUrl={p.preview_url}
              size={p.preview_size}
              templateId={templateId}
              transparency={transparency}
              subtext={subtext}
              urlType={p.url_type}
              initialSrc={p.initial_src}
              errorMessage={PREVIEW_ERROR}
            />
          </div>
        </div>
      </div>
    );
  }
}

QrDownloadForm.propTypes = {
  templates: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    default_download_size: PropTypes.number.isRequired,
    transparency: PropTypes.bool.isRequired,
  })).isRequired,
  selected_template_id: PropTypes.number.isRequired,
  sizes: PropTypes.arrayOf(PropTypes.number).isRequired,
  preview_size: PropTypes.number.isRequired,
  preview_url: PropTypes.string.isRequired,
  png_url: PropTypes.string.isRequired,
  svg_url: PropTypes.string.isRequired,
  url_type: PropTypes.string,
  subtext_limit: PropTypes.number.isRequired,
  initial_src: PropTypes.string,
};

QrDownloadForm.defaultProps = { url_type: null, initial_src: null };

export default QrDownloadForm;
